import { Flex, Modal } from "antd";
import { FC, useCallback, useEffect, useMemo } from "react";
import {
    getTaskListsThunk,
    getWorkGetScheduleThunk,
    postMessengerReceivedDelivery,
} from "tm-shared/api";
import { countText } from "tm-shared/utils";

import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import { useAsyncTaskQueue } from "@/hooks/core/useAsyncTaskQueue";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { ProgressLoader } from "@/shared/feedback";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import {
    getKpiGetPointProgressesThunk,
    getPlansByPointIdThunk,
    setUpdateUnscheduledPointsProcessing,
} from "@/store/slices/offline";
import { ModalPayloads, ModalType } from "@/types/modals";

import { getUnscheduledPointCompletionCapabilities } from "./UnscheduledPointCompletion.policy";

export interface UnscheduledPointCompletionModalProps {
    payload: ModalPayloads[ModalType.UnscheduledPointCompletion];
    onCloseModal: () => void;
}

export const UnscheduledPointCompletionModal: FC<
    UnscheduledPointCompletionModalProps
> = ({ payload, onCloseModal }) => {
    const dispatch = useAppDispatch();

    const role = useProtectedUserRoleSelector();
    const userId = useAppSelector((s) => s.user.id);
    const route = useAppSelector((s) => s.user.route);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);
    const routeList = useAppSelector((s) => s.manager.routeList);

    const { queue, progress } = useAsyncTaskQueue();

    const capabilities = useMemo(
        () => getUnscheduledPointCompletionCapabilities(role),
        [role],
    );

    useEffect(() => {
        if (!payload?.deliveryId) return;

        postMessengerReceivedDelivery({
            deliveryId: payload.deliveryId,
        });
        dispatch(setUpdateUnscheduledPointsProcessing(false));
    }, [dispatch, payload?.deliveryId]);

    const getScheduleParams = useCallback(() => {
        if (capabilities.canReloadConnectedAgentSchedule) {
            return {
                userId: activeSchedule?.userId,
                roleSlug: ROLE_CHILDREN_MAP[role],
            };
        }
        return { userId };
    }, [
        capabilities.canReloadConnectedAgentSchedule,
        activeSchedule,
        role,
        userId,
    ]);

    const getTaskParams = useCallback(
        (pointIds: string[]) => {
            const baseParams = { targetIds: pointIds };
            if (capabilities.canReloadConnectedAgentSchedule) {
                return {
                    ...baseParams,
                    roleSlug: ROLE_CHILDREN_MAP[role],
                };
            }
            return baseParams;
        },
        [capabilities.canReloadConnectedAgentSchedule, role],
    );

    const getRouteId = useCallback(() => {
        if (capabilities.canUseOwnRouteId) {
            return route?.uuid;
        }

        return Object.values(routeList).find(
            (route) => route.agentInfo.id === userId,
        )?.uuid;
    }, [capabilities.canUseOwnRouteId, route?.uuid, routeList, userId]);

    const handleConfirm = useCallback(async () => {
        try {
            await queue.add("Оновлення розкладу роботи", async () => {
                const { result } = await dispatch(
                    getWorkGetScheduleThunk(getScheduleParams()),
                ).unwrap();

                const pointIds = Object.keys(result?.points ?? {});
                if (!pointIds.length) return;

                const tasks = [
                    queue.add("Завантаження задач по ТТ", () =>
                        dispatch(
                            getTaskListsThunk(getTaskParams(pointIds)),
                        ).unwrap(),
                    ),
                ];

                if (capabilities.canLoadPlansAndFacts) {
                    const routeId = getRouteId();
                    if (routeId) {
                        tasks.push(
                            queue.add(
                                "Завантаження інформації про плани на ТТ",
                                async () => {
                                    await dispatch(
                                        getPlansByPointIdThunk({
                                            pointIds,
                                            routeId,
                                        }),
                                    ).unwrap();
                                    queue.add(
                                        "Завантаження інформації про факти на ТТ",
                                        () =>
                                            dispatch(
                                                getKpiGetPointProgressesThunk({
                                                    pointIds,
                                                }),
                                            ),
                                    );
                                },
                            ),
                        );
                    }
                }

                await Promise.all(tasks);
            });

            await queue.runParallel();
        } catch (error) {
            console.error("Failed to update work data:", error);
        } finally {
            queue.clear();
            onCloseModal();
        }
    }, [
        dispatch,
        onCloseModal,
        queue,
        getScheduleParams,
        getTaskParams,
        capabilities.canLoadPlansAndFacts,
        getRouteId,
    ]);

    const isLoaderOpen = useMemo(
        () => progress.isRunning || progress.tasks.length > 0,
        [progress.isRunning, progress.tasks.length],
    );

    return (
        <>
            <Modal
                open
                centered
                closable
                footer={null}
                title={null}
                onCancel={onCloseModal}
            >
                <Flex vertical gap={16}>
                    <PrimaryText
                        fontSize={20}
                        fontWeight="500"
                        style={{ textAlign: "center" }}
                    >
                        Додано {countText.unplanned(payload.pointsCount)} ТТ
                    </PrimaryText>

                    <SecondaryText style={{ textAlign: "center" }}>
                        Оновити робочий план зараз чи пізніше?
                    </SecondaryText>

                    <Flex vertical gap={8}>
                        <BaseButton type="primary" onClick={handleConfirm}>
                            Оновити зараз
                        </BaseButton>

                        <BaseButton type="default" onClick={onCloseModal}>
                            Оновити пізніше
                        </BaseButton>
                    </Flex>
                </Flex>
            </Modal>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};
